/**
 * Monitoring Center Badge Utilities
 *
 * Centralized badge components for consistent styling across
 * monitoring center tables and modals.
 *
 * Usage:
 * - getSeverityBadge(): For alert severity levels (critical, warning, info)
 * - getStatusBadge(): For product monitoring status (critical, warning, healthy, pending)
 * - getMetadataBadge(): For product metadata (variants, inventory, SKU)
 *
 * All badges use consistent:
 * - Icons per severity/status
 * - Polaris tone colors
 * - BadgeWithTooltip wrapper for context
 * - CSS class for standardized width
 */

import { Badge } from '@shopify/polaris';
import {
  AlertTriangleIcon,
  AlertCircleIcon,
  InfoIcon,
  CheckCircleIcon,
  ProductIcon,
  PackageIcon,
  HashtagIcon,
} from '@shopify/polaris-icons';
import { BadgeWithTooltip } from '../ui/BadgeWithTooltip';

// =============================================================================
// Types
// =============================================================================

export type AlertSeverity = 'critical' | 'warning' | 'info';
export type ProductStatus = 'critical' | 'warning' | 'healthy' | 'pending';
export type MetadataBadgeType = 'variants' | 'inventory' | 'sku';

// =============================================================================
// Tooltip Content
// =============================================================================

/** Tooltip content for alert severity badges */
export const SEVERITY_TOOLTIPS = {
  critical: 'Stock levels are critically low. Immediate action recommended.',
  warning: 'Stock is running low. Consider reordering soon.',
  info: 'Informational alert about inventory status.',
} as const;

/** Tooltip content for product status badges */
export const STATUS_TOOLTIPS = {
  critical: 'Stock levels are critically low. Immediate action recommended.',
  warning: 'Stock is running low. Consider reordering soon.',
  healthy: 'Stock levels are healthy based on current velocity.',
  pending: 'Waiting for inventory data from Shopify.',
} as const;

// =============================================================================
// Severity Badge (for alerts)
// =============================================================================

/**
 * Get a severity badge for an alert.
 * Used in: AlertsTable, AlertHistoryTable, alert-detail modal
 *
 * @param severity - The alert severity level
 * @returns Badge component with tooltip wrapped in standardized styling
 */
export function getSeverityBadge(severity: string): React.ReactNode {
  const badgeContent = {
    critical: (
      <BadgeWithTooltip
        badge={
          <Badge tone="critical" icon={AlertTriangleIcon}>
            Critical
          </Badge>
        }
        tooltipContent={SEVERITY_TOOLTIPS.critical}
      />
    ),
    warning: (
      <BadgeWithTooltip
        badge={
          <Badge tone="warning" icon={AlertCircleIcon}>
            Warning
          </Badge>
        }
        tooltipContent={SEVERITY_TOOLTIPS.warning}
      />
    ),
    info: (
      <BadgeWithTooltip
        badge={
          <Badge tone="info" icon={InfoIcon}>
            Info
          </Badge>
        }
        tooltipContent={SEVERITY_TOOLTIPS.info}
      />
    ),
  };

  const badge = badgeContent[severity as keyof typeof badgeContent] || badgeContent.info;

  return <span className="monitoring-severity-badge">{badge}</span>;
}

// =============================================================================
// Status Badge (for products)
// =============================================================================

/**
 * Get a status badge for a monitored product.
 * Used in: MonitoredProductsTable, product-settings modal
 *
 * @param status - The product status
 * @returns Badge component with tooltip wrapped in standardized styling
 */
export function getStatusBadge(status: ProductStatus): React.ReactNode {
  const badgeContent = {
    critical: (
      <BadgeWithTooltip
        badge={
          <Badge tone="critical" icon={AlertTriangleIcon}>
            Critical
          </Badge>
        }
        tooltipContent={STATUS_TOOLTIPS.critical}
      />
    ),
    warning: (
      <BadgeWithTooltip
        badge={
          <Badge tone="warning" icon={AlertCircleIcon}>
            Warning
          </Badge>
        }
        tooltipContent={STATUS_TOOLTIPS.warning}
      />
    ),
    healthy: (
      <BadgeWithTooltip
        badge={
          <Badge tone="success" icon={CheckCircleIcon}>
            Healthy
          </Badge>
        }
        tooltipContent={STATUS_TOOLTIPS.healthy}
      />
    ),
    pending: (
      <BadgeWithTooltip
        badge={
          <Badge tone="info" icon={InfoIcon}>
            Pending data
          </Badge>
        }
        tooltipContent={STATUS_TOOLTIPS.pending}
      />
    ),
  };

  return <span className="monitoring-status-badge">{badgeContent[status]}</span>;
}

/**
 * Determine product status based on alert severities and data availability.
 * Helper to convert TrackedProduct data into a ProductStatus.
 *
 * @param alertSeverities - Comma-separated string of severity levels
 * @param hasData - Whether the product has inventory data
 * @returns The appropriate ProductStatus
 */
export function getProductStatus(
  alertSeverities: string | null | undefined,
  hasData: boolean
): ProductStatus {
  if (!hasData) return 'pending';

  const severities = alertSeverities ? alertSeverities.split(',') : [];
  if (severities.includes('critical')) return 'critical';
  if (severities.includes('warning')) return 'warning';
  return 'healthy';
}

// =============================================================================
// Metadata Badges (for product info)
// =============================================================================

interface MetadataBadgeOptions {
  /** Number of variants */
  variantCount?: number;
  /** Total inventory units */
  totalInventory?: number;
  /** SKU identifier */
  sku?: string;
}

/**
 * Get metadata badges for product information.
 * Used in: product-settings modal, alert-detail modal
 *
 * @param type - The type of metadata badge
 * @param options - Badge options containing the value
 * @returns Badge component
 */
export function getMetadataBadge(
  type: MetadataBadgeType,
  options: MetadataBadgeOptions
): React.ReactNode {
  switch (type) {
    case 'variants':
      if (options.variantCount === undefined) return null;
      return (
        <span className="monitoring-metadata-badge">
          <Badge icon={ProductIcon}>
            {`${options.variantCount} variant${options.variantCount !== 1 ? 's' : ''}`}
          </Badge>
        </span>
      );

    case 'inventory':
      if (options.totalInventory === undefined) return null;
      return (
        <span className="monitoring-metadata-badge">
          <Badge icon={PackageIcon}>
            {`${options.totalInventory.toLocaleString()} units`}
          </Badge>
        </span>
      );

    case 'sku':
      if (!options.sku) return null;
      return (
        <span className="monitoring-metadata-badge">
          <Badge icon={HashtagIcon}>{options.sku}</Badge>
        </span>
      );

    default:
      return null;
  }
}

/**
 * Render all relevant metadata badges for a product.
 *
 * @param options - Badge options
 * @returns Array of badge components
 */
export function getProductMetadataBadges(options: MetadataBadgeOptions): React.ReactNode[] {
  const badges: React.ReactNode[] = [];

  if (options.variantCount !== undefined) {
    badges.push(getMetadataBadge('variants', options));
  }

  if (options.totalInventory !== undefined) {
    badges.push(getMetadataBadge('inventory', options));
  }

  if (options.sku) {
    badges.push(getMetadataBadge('sku', options));
  }

  return badges.filter(Boolean);
}
